import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Music2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SetupForm } from './SetupForm';

export default function ArtistSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [artistInfo, setArtistInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    bio: '', price: '', delivery_days: '', image_url: '', spotify_url: '',
    payout_method: 'paypal', paypal_email: '', genres: []
  });

  const validateToken = useCallback(async () => {
    try {
      const res = await api.validateSetupToken(token);
      setArtistInfo(res.data);
      setFormData(prev => ({
        ...prev,
        email: res.data.email || '',
        bio: res.data.bio || '',
        price: res.data.price?.toString() || '',
        delivery_days: res.data.delivery_days?.toString() || '14',
        image_url: res.data.image_url || '',
        spotify_url: res.data.spotify_url || '',
        genres: res.data.genres || []
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired setup link');
    } finally {
      setLoading(false);
      setValidating(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setError('No setup token provided'); setLoading(false); setValidating(false); return; }
    validateToken();
  }, [token, validateToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!artistInfo?.email && !formData.email) { toast.error('Please enter your email address'); return; }
    if (formData.email && !formData.email.includes('@')) { toast.error('Please enter a valid email address'); return; }
    if (formData.password.length < 8) { toast.error('Password must be at least 8 characters with uppercase, lowercase, and a number'); return; }
    if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/\d/.test(formData.password)) { toast.error('Password must contain uppercase, lowercase, and a number'); return; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.payout_method === 'paypal' && !formData.paypal_email) { toast.error('Please enter your PayPal email'); return; }
    
    setSaving(true);
    try {
      const submitData = {
        email: formData.email || undefined,
        password: formData.password,
        bio: formData.bio || undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        delivery_days: formData.delivery_days ? parseInt(formData.delivery_days) : undefined,
        image_url: formData.image_url || undefined,
        spotify_url: formData.spotify_url || undefined,
        payout_method: formData.payout_method,
        paypal_email: formData.payout_method === 'paypal' ? formData.paypal_email : undefined,
        genres: formData.genres.length > 0 ? formData.genres : undefined
      };
      const res = await api.completeArtistSetup(token, submitData);
      login(res.data.token, res.data.user);
      toast.success('Welcome to Tune4Tune! Your artist profile is ready.');
      navigate('/artist-dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete setup');
    } finally {
      setSaving(false);
    }
  };

  if (loading || validating) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime mx-auto mb-4" />
        <p className="text-[#A1A1AA]">Validating your setup link...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Music2 className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Setup Link Invalid</h1>
          <p className="text-[#A1A1AA] mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-[#262626]">Go to Homepage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12">
      <div className="max-w-2xl mx-auto px-4">
        <motion.div {...fadeInUp} className="text-center mb-8">
          <img src="/logo.png" alt="Tune4Tune" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold mb-2">Welcome, {artistInfo?.artist_name}!</h1>
          <p className="text-[#A1A1AA]">Complete your artist profile to start selling dubplates</p>
        </motion.div>

        <SetupForm
          formData={formData} setFormData={setFormData}
          artistInfo={artistInfo} handleSubmit={handleSubmit}
          saving={saving} showPassword={showPassword} setShowPassword={setShowPassword}
          token={token}
        />
      </div>
    </div>
  );
}
